import type { Block, ClientTier, DocMeta, TiptapDoc } from "@klarity/schema";
import { klarityTechnical, type ThemeVersionData } from "@klarity/themes";
import type { AcquireResult, LockApi } from "../../features/editing/useSoftLock";
import { ApiError, apiFetch, request } from "./client";
import type {
  ApiTokenRow,
  AuditEntry,
  BlockDiff,
  ClientOverview,
  ClientSummary,
  CreateDocInput,
  Diagram,
  DiagramSave,
  DocKind,
  DocLinkHit,
  DocLinkTarget,
  DocumentFull,
  ExportFile,
  FontInfo,
  GoreloStatus,
  InternalDoc,
  InventoryHit,
  Item,
  ItemDetail,
  ItemFilter,
  ItemPatch,
  LinkTarget,
  LockInfo,
  Me,
  MovePlan,
  Person,
  PublishedTheme,
  Revision,
  RevisionFull,
  SaveDocInput,
  SaveOutcome,
  SearchHit,
  Site,
  SnippetPart,
  Space,
  SpaceMember,
  SyncOutcome,
  Template,
  ThemeSummary,
  ThemeVersion,
  ThemeVersionRow,
  TreeDoc,
  UserRow,
} from "./models";

// ── wire shapes (plan 03 "HTTP surface") ────────────────────────────────────
type LockDto = { userId: string; name: string; lockedAt: string; stale: boolean };
type DocDto = {
  id: string;
  spaceId: string;
  parentId: string | null;
  position: number;
  title: string;
  kind: DocKind;
  meta: DocMeta;
  themeId: string | null;
  layoutKey: string;
  content: TiptapDoc;
  version: number;
  lock: LockDto | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: Person | null;
  archivedAt: string | null;
};
type TreeNodeDto = TreeDoc & { archivedAt: string | null };
type DiffOpDto =
  | { op: "same"; oldIndex: number; newIndex: number; block: Block }
  | { op: "changed"; oldIndex: number; newIndex: number; old: Block; new: Block }
  | { op: "removed"; oldIndex: number; old: Block }
  | { op: "added"; newIndex: number; new: Block };
type SearchDto = Omit<SearchHit, "snippet"> & { snippet: string };

export const isUuid = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
const enc = encodeURIComponent;

const toLock = (l: LockDto): LockInfo => ({
  holder: { id: l.userId, name: l.name },
  lockedAt: l.lockedAt,
  stale: l.stale,
});
const isLockDto = (v: unknown): v is LockDto =>
  typeof v === "object" && v !== null && "userId" in v && "name" in v && "lockedAt" in v;
const toDoc = (d: DocDto): DocumentFull => ({
  id: d.id,
  spaceId: d.spaceId,
  parentId: d.parentId,
  title: d.title,
  kind: d.kind,
  meta: d.meta,
  themeId: d.themeId,
  layoutKey: d.layoutKey,
  content: d.content,
  version: d.version,
  lock: d.lock ? toLock(d.lock) : null,
  updatedAt: d.updatedAt,
  updatedBy: d.updatedBy,
  archivedAt: d.archivedAt,
});

const ENTITIES: Record<string, string> = { lt: "<", gt: ">", amp: "&", quot: '"', "#39": "'", "#x27": "'" };
const decode = (s: string) => s.replace(/&(lt|gt|amp|quot|#39|#x27);/g, (m, e: string) => ENTITIES[e] ?? m);

/** Plan 03 snippets are HTML-escaped text with <mark> only. Parse to parts; never render as HTML. */
export function parseSnippet(html: string): SnippetPart[] {
  const parts: SnippetPart[] = [];
  let last = 0;
  for (const m of html.matchAll(/<mark>([\s\S]*?)<\/mark>/g)) {
    const at = m.index ?? 0;
    if (at > last) parts.push({ text: decode(html.slice(last, at)), match: false });
    parts.push({ text: decode(m[1] ?? ""), match: true });
    last = at + m[0].length;
  }
  if (last < html.length) parts.push({ text: decode(html.slice(last)), match: false });
  return parts.filter((p) => p.text !== "");
}

export function parseWarnings(header: string | null): string[] {
  if (!header) return [];
  try {
    const v: unknown = JSON.parse(decodeURIComponent(header));
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function filenameFrom(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch {
      // fall through to the plain filename
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return plain?.[1]?.trim() || fallback;
}

const CAUSES: Record<string, string> = {
  timeout: "The PDF took longer than 60 seconds to render. Try again; very long documents may need splitting.",
  sidecar_unavailable: "The PDF service is not available right now. Try again in a minute. DOCX export still works.",
  theme_render_error: "The theme could not render this document. An admin can check the theme's layouts and CSS.",
};

export function exportErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const cause = typeof e.details.cause === "string" ? e.details.cause : "";
    return CAUSES[cause] ?? e.message;
  }
  return e instanceof Error ? e.message : "The export failed.";
}

async function fileFrom(res: Response, fallback: string): Promise<ExportFile> {
  return {
    blob: await res.blob(),
    filename: filenameFrom(res.headers.get("Content-Disposition"), fallback),
    warnings: parseWarnings(res.headers.get("X-KD-Warnings")),
    warningsTruncated: Number(res.headers.get("X-KD-Warnings-Truncated") ?? 0) || 0,
  };
}
async function exportCall<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (e) {
    if (e instanceof ApiError && e.code === "export_failed") {
      throw new ApiError(e.status, e.code, exportErrorMessage(e), e.details);
    }
    throw e;
  }
}
const multipart = (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return fd;
};

let uploadChain: Promise<unknown> = Promise.resolve();
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
/**
 * Uploads run one at a time per tab (paste, drop and theme uploads share the queue). The server admits two uploads
 * at once and answers `503 busy` with `Retry-After`; that is retried after the given seconds, up to 3 times.
 */
function upload<T>(path: string, file: File): Promise<T> {
  const run = async (): Promise<T> => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await request<T>(path, { method: "POST", body: multipart(file) });
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 503 && e.code === "busy") || attempt >= 3) throw e;
        await sleep(1000 * (typeof e.details.retryAfter === "number" ? e.details.retryAfter : 5));
      }
    }
  };
  const p = uploadChain.then(run, run);
  uploadChain = p.catch(() => {});
  return p;
}

const locks: LockApi = {
  async acquire(docId, takeover): Promise<AcquireResult> {
    try {
      await request<LockDto>(`/api/ui/documents/${enc(docId)}/lock`, { method: "POST", json: { takeover } });
      return { status: "held" };
    } catch (e) {
      if (e instanceof ApiError && (e.code === "locked" || e.code === "lock_stale") && isLockDto(e.details.lock)) {
        return { status: "blocked", lock: toLock({ ...e.details.lock, stale: e.code === "lock_stale" }) };
      }
      throw e;
    }
  },
  async heartbeat(docId) {
    try {
      await request<LockDto>(`/api/ui/documents/${enc(docId)}/lock/heartbeat`, { method: "POST" });
      return "held";
    } catch (e) {
      if (e instanceof ApiError && (e.code === "lock_lost" || e.status === 404)) return "lost";
      throw e;
    }
  },
  async release(docId, keepalive) {
    await request<void>(`/api/ui/documents/${enc(docId)}/lock`, { method: "DELETE", keepalive });
  },
};

async function resolveCode(code: string): Promise<string> {
  // The resolve route answers 400 for a malformed code; a bad /d/<ref> link is simply "not found".
  if (!/^[A-Za-z0-9-]{1,80}$/.test(code)) throw new ApiError(404, "not_found", "Document not found");
  const map = await request<Record<string, { id: string }>>(`/api/ui/doc-links/resolve?codes=${enc(code)}`);
  const hit = map[code];
  if (!hit) throw new ApiError(404, "not_found", "Document not found");
  return hit.id;
}

export const api = {
  me: () => request<Me>("/api/ui/me"),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),

  spaces: {
    list: () => request<Space[]>("/api/ui/spaces"),
    /** Admin only: archived spaces, clients included. `?archived=1` mixes them with live ones; keep the archived. */
    listArchived: async (): Promise<Space[]> =>
      (await request<Space[]>("/api/ui/spaces?archived=1")).filter((s) => Boolean(s.archivedAt)),
    unarchive: async (id: string): Promise<void> => {
      await request(`/api/ui/spaces/${enc(id)}/unarchive`, { method: "POST" });
    },
    create: (input: { slug: string; name: string; icon?: string; restricted?: boolean }) =>
      request<Space>("/api/ui/spaces", { method: "POST", json: input }),
    update: (
      id: string,
      patch: { name?: string; icon?: string | null; restricted?: boolean; position?: number; archived?: boolean },
    ) => request<Space>(`/api/ui/spaces/${enc(id)}`, { method: "PATCH", json: patch }),
    /** Admin only: deletes an internal space, an unlinked client or a hidden client for good. 409 space_not_deletable. */
    remove: (id: string) =>
      request<{ ok: true; documents: number; sites: number; items: number }>(`/api/ui/spaces/${enc(id)}`, {
        method: "DELETE",
      }),
    members: (id: string) => request<SpaceMember[]>(`/api/ui/spaces/${enc(id)}/members`),
    setMembers: (id: string, members: Array<Pick<SpaceMember, "principalType" | "principalId">>) =>
      request<SpaceMember[]>(`/api/ui/spaces/${enc(id)}/members`, { method: "PUT", json: { members } }),
  },
  tree: async (spaceId: string): Promise<TreeDoc[]> =>
    (await request<TreeNodeDto[]>(`/api/ui/spaces/${enc(spaceId)}/tree`)).map(({ archivedAt: _a, ...n }) => n),
  /** Admin only: the tree including archived documents (plan 03 `?archived=1`). */
  archivedTree: async (spaceId: string): Promise<Array<TreeDoc & { archivedAt: string }>> =>
    (await request<TreeNodeDto[]>(`/api/ui/spaces/${enc(spaceId)}/tree?archived=1`)).filter(
      (n): n is TreeNodeDto & { archivedAt: string } => n.archivedAt !== null,
    ),
  templates: () => request<Template[]>("/api/ui/templates"),
  /** Every live document of the Handbook's topics the user sees (no wiki pages): the Handbook page's counts. */
  internalDocuments: () => request<InternalDoc[]>("/api/ui/internal/documents"),

  documents: {
    async get(ref: string): Promise<DocumentFull> {
      const id = isUuid(ref) ? ref : await resolveCode(ref);
      return toDoc(await request<DocDto>(`/api/ui/documents/${enc(id)}`));
    },
    async create(input: CreateDocInput): Promise<DocumentFull> {
      // The server refuses `themeId: null`; without a theme it picks the default theme.
      const { themeId, ...rest } = input;
      return toDoc(
        await request<DocDto>("/api/ui/documents", { method: "POST", json: themeId ? { ...rest, themeId } : rest }),
      );
    },
    async save(id: string, input: SaveDocInput): Promise<SaveOutcome> {
      try {
        const r = await request<{ version: number; updatedAt: string }>(`/api/ui/documents/${enc(id)}`, {
          method: "PUT",
          json: input,
        });
        return { status: "saved", version: r.version };
      } catch (e) {
        if (e instanceof ApiError && e.code === "conflict") return { status: "conflict" };
        if (e instanceof ApiError && e.code === "lock_lost") return { status: "locked" };
        throw e;
      }
    },
    move: async (id: string, plan: MovePlan): Promise<void> => {
      await request(`/api/ui/documents/${enc(id)}/move`, {
        method: "POST",
        json: { parentId: plan.parentId, position: plan.position },
      });
    },
    /** To the top level of another topic or client. No position: the server puts the document last. */
    moveTo: async (id: string, spaceId: string): Promise<void> => {
      await request(`/api/ui/documents/${enc(id)}/move`, { method: "POST", json: { spaceId, parentId: null } });
    },
    archive: async (id: string): Promise<void> => {
      await request(`/api/ui/documents/${enc(id)}/archive`, { method: "POST" });
    },
    unarchive: async (id: string): Promise<void> => {
      await request(`/api/ui/documents/${enc(id)}/unarchive`, { method: "POST" });
    },
  },

  locks,

  revisions: {
    list: (docId: string) => request<Revision[]>(`/api/ui/documents/${enc(docId)}/revisions`),
    get: (docId: string, version: number) =>
      request<RevisionFull>(`/api/ui/documents/${enc(docId)}/revisions/${version}`),
    async diff(docId: string, version: number): Promise<BlockDiff[]> {
      const r = await request<{ ops: DiffOpDto[] }>(`/api/ui/documents/${enc(docId)}/revisions/${version}/diff`);
      return r.ops.map((op): BlockDiff => {
        if (op.op === "same") return { kind: "same", block: op.block };
        if (op.op === "changed") return { kind: "changed", before: op.old, after: op.new };
        if (op.op === "removed") return { kind: "removed", block: op.old };
        return { kind: "added", block: op.new };
      });
    },
    restore: (docId: string, version: number, currentVersion: number) =>
      request<{ version: number }>(`/api/ui/documents/${enc(docId)}/revisions/${version}/restore`, {
        method: "POST",
        json: { version: currentVersion },
      }),
  },

  attachments: {
    url: (src: string) => (src.startsWith("att:") ? `/api/ui/files/${enc(src.slice(4))}` : ""),
    async uploadImage(docId: string, file: File): Promise<{ src: string }> {
      const r = await upload<{ src: string }>(`/api/ui/documents/${enc(docId)}/attachments`, file);
      return { src: r.src };
    },
  },

  exports: {
    download: (docId: string, format: "pdf" | "docx") =>
      exportCall(async () =>
        fileFrom(await apiFetch(`/api/ui/documents/${enc(docId)}/export.${format}`), `${docId}.${format}`),
      ),
    preview: (docId: string, unsaved?: { title?: string; meta?: DocMeta; content?: TiptapDoc }) =>
      exportCall(async () =>
        (await apiFetch(`/api/ui/documents/${enc(docId)}/preview.pdf`, { method: "POST", json: unsaved ?? {} })).blob(),
      ),
  },

  async search(q: string, opts: { spaceId?: string; kind?: DocKind; limit?: number } = {}): Promise<SearchHit[]> {
    const p = new URLSearchParams({ q });
    if (opts.spaceId) p.set("spaceId", opts.spaceId);
    if (opts.kind) p.set("kind", opts.kind);
    if (opts.limit) p.set("limit", String(opts.limit));
    return (await request<SearchDto[]>(`/api/ui/search?${p}`)).map((h) => ({
      ...h,
      snippet: parseSnippet(h.snippet ?? ""),
    }));
  },
  docLinks: (q: string) => request<DocLinkHit[]>(`/api/ui/doc-links?q=${enc(q)}`),

  themes: {
    list: () => request<ThemeSummary[]>("/api/ui/themes"),
    create: (name: string) => request<ThemeSummary>("/api/ui/themes", { method: "POST", json: { name } }),
    update: (id: string, patch: { name?: string; isDefault?: boolean; archived?: boolean }) =>
      request<ThemeSummary>(`/api/ui/themes/${enc(id)}`, { method: "PATCH", json: patch }),
    /** The document's own theme by id (archived themes included); the default theme when the document has none. */
    async published(themeId: string | null): Promise<PublishedTheme> {
      const themes = await request<ThemeSummary[]>("/api/ui/themes");
      const id = themeId ?? themes.find((x) => x.isDefault)?.id ?? null;
      const fallback = (): PublishedTheme => ({
        themeId: id,
        versionId: null,
        data: klarityTechnical.data,
        fallback: true,
      });
      if (!id) return fallback();
      try {
        const v = await request<ThemeVersion>(`/api/ui/themes/${enc(id)}/published`);
        return { themeId: id, versionId: v.id, data: v.data, fallback: false };
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return fallback();
        throw e;
      }
    },
    versions: (id: string) => request<ThemeVersionRow[]>(`/api/ui/themes/${enc(id)}/versions`),
    version: (id: string, versionId: string) =>
      request<ThemeVersion>(`/api/ui/themes/${enc(id)}/versions/${enc(versionId)}`),
    draft: (id: string) => request<ThemeVersion>(`/api/ui/themes/${enc(id)}/draft`, { method: "POST" }),
    saveDraft: (id: string, data: ThemeVersionData) =>
      request<ThemeVersion>(`/api/ui/themes/${enc(id)}/draft`, { method: "PUT", json: { data } }),
    discardDraft: (id: string) => request<void>(`/api/ui/themes/${enc(id)}/draft`, { method: "DELETE" }),
    previewDraft: (id: string, opts: { documentId?: string; layoutKey?: string }) =>
      exportCall(async () =>
        (await apiFetch(`/api/ui/themes/${enc(id)}/draft/preview.pdf`, { method: "POST", json: opts })).blob(),
      ),
    publish: (id: string) =>
      request<{ id: string; version: number }>(`/api/ui/themes/${enc(id)}/draft/publish`, { method: "POST" }),
    restore: (id: string, versionId: string) =>
      request<{ id: string; version: number }>(`/api/ui/themes/${enc(id)}/versions/${enc(versionId)}/restore`, {
        method: "POST",
      }),
    uploadFont: (id: string, file: File) => upload<FontInfo>(`/api/ui/themes/${enc(id)}/fonts`, file),
    uploadImage: (id: string, file: File) =>
      upload<{ src: string; mime: string; width: number; height: number }>(`/api/ui/themes/${enc(id)}/images`, file),
  },

  // ── Clients, sites and inventory. They come from Gorelo; a client id is its space id. ──────
  clients: {
    /** The client list and whether Gorelo is connected and when the last good pass ended: one request. */
    overview: (): Promise<ClientOverview> => request<ClientOverview>("/api/ui/clients"),
    /** Syncs this one client. 409 sync_running, client_unlinked, gorelo_not_configured; 502 gorelo_failed. */
    sync: (id: string) => request<SyncOutcome>(`/api/ui/clients/${enc(id)}/sync`, { method: "POST" }),
    /** Admin only. `null` clears the tier (Uncategorized). 403 for anyone else, 404 when the client is gone. */
    setTier: (id: string, tier: ClientTier | null) =>
      request<ClientSummary>(`/api/ui/clients/${enc(id)}`, { method: "PATCH", json: { tier } }),
  },
  gorelo: {
    /** A full pass. `applyRemovals` (admin) runs it without the safety stop. 409 safety_stop, sync_running. */
    sync: (opts: { applyRemovals?: boolean } = {}) =>
      request<SyncOutcome>("/api/ui/gorelo/sync", { method: "POST", json: opts }),
    /** Admin only. */
    status: () => request<GoreloStatus>("/api/ui/gorelo/status"),
  },
  sites: {
    list: async (clientId: string): Promise<Site[]> =>
      (await request<{ items: Site[] }>(`/api/ui/clients/${enc(clientId)}/sites`)).items,
    /** The notes are the one field of a site that Docs owns. 409 client_unlinked. */
    updateNotes: (id: string, notes: string) =>
      request<Site>(`/api/ui/sites/${enc(id)}`, { method: "PATCH", json: { notes } }),
  },
  items: {
    async list(clientId: string, filter: ItemFilter = {}): Promise<Item[]> {
      const p = new URLSearchParams();
      if (filter.category) p.set("category", filter.category);
      if (filter.siteId) p.set("site", filter.siteId);
      if (filter.q?.trim()) p.set("q", filter.q.trim());
      const qs = p.toString();
      return (await request<{ items: Item[] }>(`/api/ui/clients/${enc(clientId)}/items${qs ? `?${qs}` : ""}`)).items;
    },
    get: (id: string) => request<ItemDetail>(`/api/ui/items/${enc(id)}`),
    /** Partial. 409 field_from_gorelo for a device's manufacturer, model or warranty start; 409 client_unlinked. */
    update: (id: string, patch: ItemPatch) =>
      request<Item>(`/api/ui/items/${enc(id)}`, { method: "PATCH", json: patch }),
  },
  /** A site's network diagram. A save replaces it whole: 409 diagram_conflict, 409 client_unlinked, 422 diagram_item_unavailable. */
  diagram: {
    get: (siteId: string) => request<Diagram>(`/api/ui/sites/${enc(siteId)}/diagram`),
    save: (siteId: string, body: DiagramSave) =>
      request<Diagram>(`/api/ui/sites/${enc(siteId)}/diagram`, { method: "PUT", json: body }),
  },
  links: {
    get: async (docId: string): Promise<DocLinkTarget[]> =>
      (await request<{ items: DocLinkTarget[] }>(`/api/ui/documents/${enc(docId)}/links`)).items,
    /** Replaces the whole set. Not tied to the edit lock; no revision is written. 400 invalid_link. */
    set: async (docId: string, links: LinkTarget[]): Promise<DocLinkTarget[]> =>
      (
        await request<{ items: DocLinkTarget[] }>(`/api/ui/documents/${enc(docId)}/links`, {
          method: "PUT",
          json: { links: links.map(({ targetType, targetId }) => ({ targetType, targetId })) },
        })
      ).items,
  },
  /** Sites and items by name or serial number, visible clients only (2–100 characters). */
  searchInventory: async (q: string): Promise<InventoryHit[]> =>
    (await request<{ items: InventoryHit[] }>(`/api/ui/search/inventory?q=${enc(q.trim())}`)).items,

  users: {
    list: () => request<UserRow[]>("/api/ui/users"),
    deactivate: async (id: string) => {
      await request(`/api/ui/users/${enc(id)}/deactivate`, { method: "POST" });
    },
    /** Resolves to `active`: false means access returns only at the next sign-in, in a KD group. */
    reactivate: async (id: string): Promise<boolean> =>
      (await request<{ ok: true; active: boolean }>(`/api/ui/users/${enc(id)}/reactivate`, { method: "POST" })).active,
  },
  tokens: {
    list: () => request<ApiTokenRow[]>("/api/ui/tokens"),
    create: (input: { name: string; scopes: Array<"read" | "write">; expiresInDays: number }) =>
      request<{ id: string; name: string; scopes: Array<"read" | "write">; expiresAt: string; token: string }>(
        "/api/ui/tokens",
        {
          method: "POST",
          json: input,
        },
      ),
    revoke: (id: string) => request<void>(`/api/ui/tokens/${enc(id)}`, { method: "DELETE" }),
  },
  audit: (params: { before?: number; action?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    if (params.before !== undefined) p.set("before", String(params.before));
    if (params.action) p.set("action", params.action);
    p.set("limit", String(params.limit ?? 50));
    return request<{ items: AuditEntry[]; next: number | null }>(`/api/ui/audit?${p}`);
  },
};
